import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { adminBookingsApi, adminSlotOverridesApi } from '@api/client';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';
import { useActiveCenter, ALL_CENTERS } from '@hooks/useActiveCenter';
import { BookingDetailDrawer } from './BookingDetailDrawer';
import { CalendarWeek } from './CalendarWeek';
import { CalendarDay } from './CalendarDay';
import { CalendarMonth } from './CalendarMonth';
import { CalendarAgenda } from './CalendarAgenda';
import {
  ACTIVE_BOOKING_STATUSES,
  MONTHS_ES,
  addDays,
  fmtDateLong,
  fmtLocalTime,
  fmtTime,
  getMondayOf,
  isoDate,
  DEFAULT_BLOCK_FORM,
  type BlockForm,
} from './calendar-shared';

type CalendarView = 'day' | 'week' | 'month' | 'agenda';

const VIEW_LABELS: Record<CalendarView, string> = {
  day: 'Día',
  week: 'Semana',
  month: 'Mes',
  agenda: 'Agenda',
};

export function CalendarPage() {
  const [view, setView] = useState<CalendarView>('week');
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [form, setForm] = useState<BlockForm>(DEFAULT_BLOCK_FORM);
  const [openId, setOpenId] = useState<string | null>(null);
  const qc = useQueryClient();

  // Shared centre filter — the page consumes the value implicitly via the
  // `X-Center-Id` header injected by apiFetch, but rendering the dropdown
  // here lets the operator pick a centre without leaving the calendar.
  const { activeCenterId, setActiveCenterId, availableCenters, canSwitch, supportsAll, isSuper } = useActiveCenter();

  // ── Range computation per view ─────────────────────────────────────────
  const { rangeStart, rangeEnd, headerSub } = useMemo(() => {
    if (view === 'day') {
      const iso = isoDate(anchor);
      return { rangeStart: iso, rangeEnd: iso, headerSub: `Día · ${fmtDateLong(anchor)}` };
    }
    if (view === 'week') {
      const mon = getMondayOf(anchor);
      const sun = addDays(mon, 6);
      const m0 = MONTHS_ES[mon.getMonth()] ?? '';
      const m1 = MONTHS_ES[sun.getMonth()] ?? '';
      const sub = m0 === m1
        ? `Semana del ${String(mon.getDate())}–${String(sun.getDate())} de ${m0}`
        : `Semana del ${String(mon.getDate())} ${m0} – ${String(sun.getDate())} ${m1}`;
      return { rangeStart: isoDate(mon), rangeEnd: isoDate(sun), headerSub: sub };
    }
    if (view === 'month') {
      const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return {
        rangeStart: isoDate(first),
        rangeEnd: isoDate(last),
        headerSub: `${MONTHS_ES[anchor.getMonth()] ?? ''} ${String(anchor.getFullYear())}`,
      };
    }
    // agenda — next 14 days from anchor
    const end = addDays(anchor, 13);
    return { rangeStart: isoDate(anchor), rangeEnd: isoDate(end), headerSub: 'Próximos 14 días' };
  }, [view, anchor]);

  // ── Data fetching ──────────────────────────────────────────────────────
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['bookings', 'calendar', view, rangeStart, rangeEnd],
    queryFn: () =>
      adminBookingsApi.list({
        dateFrom: rangeStart,
        dateTo: rangeEnd,
        perPage: '500',
      }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  const { data: overridesData } = useQuery({
    queryKey: ['slot-overrides', rangeStart],
    queryFn: () => adminSlotOverridesApi.list(rangeStart),
    staleTime: 30_000,
  });

  const createOverride = useMutation({
    mutationFn: (d: Parameters<typeof adminSlotOverridesApi.create>[0]) =>
      adminSlotOverridesApi.create(d),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['slot-overrides'] });
      void qc.invalidateQueries({ queryKey: ['bookings', 'calendar'] });
      setShowBlockForm(false);
      setForm(DEFAULT_BLOCK_FORM);
    },
  });

  const deleteOverride = useMutation({
    mutationFn: (id: string) => adminSlotOverridesApi.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['slot-overrides'] }),
  });

  const bookings = data?.data ?? [];
  const overrides = overridesData ?? [];

  // Bookings that overlap an active slot override
  const blockConflicts = useMemo(() => {
    if (overrides.length === 0) return [];
    return bookings.filter((b) => {
      if (!ACTIVE_BOOKING_STATUSES.has(b.status)) return false;
      const bStart = new Date(b.startTime).getTime();
      const bEnd = new Date(b.endTime).getTime();
      return overrides.some((ov) => {
        const ovStart = new Date(ov.startTime).getTime();
        const ovEnd = new Date(ov.endTime).getTime();
        if (ovStart >= bEnd || ovEnd <= bStart) return false;
        return ov.staffId === null || ov.staffId === b.staffId;
      });
    });
  }, [bookings, overrides]);

  const openBooking = bookings.find((b) => b.id === openId) ?? null;

  // ── Navigation per view ────────────────────────────────────────────────
  function goBack() {
    if (view === 'day') setAnchor((d) => addDays(d, -1));
    else if (view === 'week') setAnchor((d) => addDays(d, -7));
    else if (view === 'month') setAnchor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    else setAnchor((d) => addDays(d, -14));
  }
  function goForward() {
    if (view === 'day') setAnchor((d) => addDays(d, 1));
    else if (view === 'week') setAnchor((d) => addDays(d, 7));
    else if (view === 'month') setAnchor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    else setAnchor((d) => addDays(d, 14));
  }
  function goToday() { setAnchor(new Date()); }

  function handleSubmitBlock(e: React.SyntheticEvent) {
    e.preventDefault();
    createOverride.mutate({
      startTime: fmtLocalTime(form.startHour, form.date),
      endTime:   fmtLocalTime(form.endHour,   form.date),
      reason:    form.reason || undefined,
    });
  }

  function handlePickDate(iso: string) {
    const [yStr, mStr, dStr] = iso.split('-');
    setAnchor(new Date(Number(yStr), Number(mStr) - 1, Number(dStr)));
    setView('day');
  }

  return (
    <div>
      <PageHead
        title="Calendario"
        sub={headerSub}
        actions={
          <div className="seg">
            {(Object.entries(VIEW_LABELS) as [CalendarView, string][]).map(([k, l]) => (
              <button
                key={k}
                type="button"
                className="seg-btn"
                data-active={view === k}
                onClick={() => { setView(k); }}
              >
                {l}
              </button>
            ))}
          </div>
        }
      />

      <div className="toolbar">
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <button className="icon-btn" onClick={goBack} aria-label="Anterior"><Icons.ChevL size={14} /></button>
          <button className="btn" data-size="sm" onClick={goToday}>Hoy</button>
          <button className="icon-btn" onClick={goForward} aria-label="Siguiente"><Icons.ChevR size={14} /></button>
        </div>
        {canSwitch && (
          <select
            className="select"
            value={activeCenterId}
            onChange={(e) => { setActiveCenterId(e.target.value); }}
            aria-label="Filtrar por centro"
            style={{ width: 'auto' }}
          >
            {supportsAll && (
              <option value={ALL_CENTERS}>{isSuper ? 'Todos los centros' : 'Todos mis centros'}</option>
            )}
            {availableCenters.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <div className="toolbar-spacer" />
        {isFetching && <span style={{ fontSize: 12, color: 'var(--fg-soft)' }}>Actualizando…</span>}
        {(view === 'week' || view === 'day') && (
          <button
            className="btn"
            data-size="sm"
            onClick={() => { setShowBlockForm((v) => !v); }}
          >
            {showBlockForm ? 'Cancelar' : 'Bloquear horario'}
          </button>
        )}
      </div>

      {blockConflicts.length > 0 && (
        <div className="cal-conflicts">
          <span aria-hidden="true" style={{ fontSize: 16 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <strong>
              {blockConflicts.length === 1
                ? 'Hay 1 reserva activa en una franja bloqueada.'
                : `Hay ${String(blockConflicts.length)} reservas activas en franjas bloqueadas.`}
            </strong>{' '}
            Haz clic en cada una para revisarla o cancelarla.{' '}
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              {blockConflicts.map((b) => (
                <button
                  key={b.id}
                  className="btn"
                  data-size="sm"
                  onClick={() => { setOpenId(b.id); }}
                  style={{ fontSize: 11.5, padding: '2px 8px' }}
                >
                  {b.customerName.split(' ')[0]} · {b.startTime.slice(0, 10)} {fmtTime(b.startTime)}
                </button>
              ))}
            </span>
          </div>
        </div>
      )}

      {showBlockForm && (view === 'week' || view === 'day') && (
        <form onSubmit={handleSubmitBlock} className="cal-block-form">
          <div className="field" style={{ flex: '0 0 auto' }}>
            <label className="field-label">Fecha</label>
            <input
              type="date"
              className="input"
              value={form.date}
              onChange={(e) => { setForm((f) => ({ ...f, date: e.target.value })); }}
              required
            />
          </div>
          <div className="field" style={{ flex: '0 0 auto' }}>
            <label className="field-label">Desde</label>
            <input
              type="time"
              className="input"
              value={form.startHour}
              onChange={(e) => { setForm((f) => ({ ...f, startHour: e.target.value })); }}
              required
            />
          </div>
          <div className="field" style={{ flex: '0 0 auto' }}>
            <label className="field-label">Hasta</label>
            <input
              type="time"
              className="input"
              value={form.endHour}
              onChange={(e) => { setForm((f) => ({ ...f, endHour: e.target.value })); }}
              required
            />
          </div>
          <div className="field" style={{ flex: 1, minWidth: 140 }}>
            <label className="field-label">Motivo (opcional)</label>
            <input
              type="text"
              className="input"
              value={form.reason}
              maxLength={200}
              placeholder="ej. Festivo, Formación…"
              onChange={(e) => { setForm((f) => ({ ...f, reason: e.target.value })); }}
            />
          </div>
          <button
            type="submit"
            className="btn"
            data-variant="primary"
            data-size="sm"
            disabled={createOverride.isPending}
          >
            {createOverride.isPending ? 'Guardando…' : 'Guardar bloqueo'}
          </button>
          {createOverride.isError && (
            <span style={{ fontSize: 12, color: 'var(--danger)' }}>Error al guardar</span>
          )}
        </form>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 64, color: 'var(--fg-muted)' }}>
          <span className="spinner" />
        </div>
      ) : view === 'week' ? (
        <CalendarWeek
          monday={getMondayOf(anchor)}
          bookings={bookings}
          overrides={overrides}
          onOpenBooking={setOpenId}
          onDeleteOverride={(id) => { deleteOverride.mutate(id); }}
        />
      ) : view === 'day' ? (
        <CalendarDay
          date={anchor}
          bookings={bookings}
          overrides={overrides}
          onOpenBooking={setOpenId}
          onDeleteOverride={(id) => { deleteOverride.mutate(id); }}
        />
      ) : view === 'month' ? (
        <CalendarMonth date={anchor} bookings={bookings} onPickDate={handlePickDate} />
      ) : (
        <CalendarAgenda bookings={bookings} onOpenBooking={setOpenId} />
      )}

      {openBooking && (
        <BookingDetailDrawer booking={openBooking} onClose={() => { setOpenId(null); }} />
      )}
    </div>
  );
}
